import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { initVersionedSchema } from '@principle-theorem/editor';
import {
  EditorPresetsService,
  renderRawTemplate,
} from '@principle-theorem/ng-interactions';
import {
  DateSelectorComponent,
  InformationBoxComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  MOMENT_DATEPICKER_PROVIDERS,
  NgCommonModule,
  formControlChanges$,
} from '@principle-theorem/ng-shared';
import {
  NgTemplatingModule,
  TemplateEditorTextareaComponent,
} from '@principle-theorem/ng-templating';
import {
  AutomatedNotification,
  MOCK_ALL_RESOLVER,
  TemplateDefinition,
  getScopedConditions,
} from '@principle-theorem/principle-core';
import {
  AutomatedNotificationType,
  AutomationContext,
  ConditionLogicConfigurationCollection,
  ConditionLogicId,
  IAutomationTiming,
  TemplateScope,
  isAutomatedNotificationConfiguration,
  type IAutomatedNotification,
  type IAutomatedNotificationConfiguration,
  type IAutomationConfiguration,
  type ITemplateContext,
  type ITemplateDefinition,
  type ITemplateScopeData,
  type TemplateType,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  TIME_FORMAT_24HR,
  filterUndefined,
  isChanged$,
  mergeDayAndTime,
  snapshot,
  toMoment,
  toTimestamp,
  type DocumentReference,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import {
  BehaviorSubject,
  ReplaySubject,
  Subject,
  combineLatest,
  defer,
  of,
  type Observable,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import {
  AUTOMATION_CONDITIONS_HELP_TEXT,
  AUTOMATION_CUSTOM_CONFIGURATION_HELP_TEXT,
} from '../automated-notification-configuration-edit/automated-notification-configuration-form';
import { AutomationTimingFormComponent } from '../automation-timing-form/automation-timing-form.component';
import { AutomatedNotificationConfigPreviewComponent } from '../notification-dialog/automated-notification-config-preview/automated-notification-config-preview.component';
import {
  AutomatedNotificationFormHelpers,
  NotificationDialogForm,
} from '../notification-dialog/notification-dialog-form';

export interface IAutomatedNotificationReturnData
  extends IAutomatedNotification {
  triggerDate?: Timestamp;
  configRef?: DocumentReference<IAutomationConfiguration>;
}

@Component({
  selector: 'pr-automated-notification-edit',
  templateUrl: './automated-notification-edit.component.html',
  styleUrls: ['./automated-notification-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [...MOMENT_DATEPICKER_PROVIDERS],
  exportAs: 'prAutomatedNotificationEdit',
  imports: [
    AutomatedNotificationConfigPreviewComponent,
    AutomationTimingFormComponent,
    DateSelectorComponent,
    InformationBoxComponent,
    MatDatepickerModule,
    MatIcon,
    MatIconButton,
    MatInputModule,
    MatSelectModule,
    MatSlideToggle,
    MatTooltip,
    NgCommonModule,
    NgTemplatingModule,
    ReactiveFormsModule,
    TemplateEditorTextareaComponent,
  ],
})
export class AutomatedNotificationEditComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject();
  private _scopeData$: Observable<ITemplateScopeData>;
  formHelpers = AutomatedNotificationFormHelpers;
  templateType$: Observable<TemplateType>;
  templateContext$: Observable<ITemplateContext>;
  useCustomTemplate$ = new BehaviorSubject<boolean>(true);
  config$: Observable<WithRef<IAutomatedNotificationConfiguration> | undefined>;
  configTemplate$: Observable<ITemplateDefinition | undefined>;
  form = new NotificationDialogForm();
  hasSubjectInput$: Observable<boolean>;
  hasScope$: Observable<boolean>;
  availableConditions$: Observable<ConditionLogicId[]>;
  defaultCondition = ConditionLogicId.Always;
  automationConditionsHelp = AUTOMATION_CONDITIONS_HELP_TEXT;
  customConfigurationHelp = AUTOMATION_CUSTOM_CONFIGURATION_HELP_TEXT;

  notification$ = new BehaviorSubject<IAutomatedNotification | undefined>(
    undefined
  );
  useRelativeTime$ = new BehaviorSubject<boolean>(false);
  scope$ = new ReplaySubject<TemplateScope>(1);
  scopeData$ = new ReplaySubject<ITemplateScopeData>(1);
  triggerDate$ = new BehaviorSubject<Timestamp | undefined>(undefined);
  configRef$ = new BehaviorSubject<
    DocumentReference<IAutomationConfiguration> | undefined
  >(undefined);
  context$ = new ReplaySubject<AutomationContext>(1);
  isAppointmentContext$ = this.context$.pipe(
    map((context) => context === AutomationContext.Appointment)
  );
  isMarketingContext$ = this.context$.pipe(
    map((context) => context === AutomationContext.Marketing)
  );

  @Input()
  set context(context: AutomationContext) {
    if (context) {
      this.context$.next(context);
    }
  }

  @Input()
  set configRef(configRef: DocumentReference<IAutomationConfiguration>) {
    if (configRef) {
      this.configRef$.next(configRef);
    }
  }

  @Input()
  set triggerDate(triggerDate: Timestamp) {
    if (triggerDate) {
      this.triggerDate$.next(triggerDate);
    }
  }

  @Input()
  set scopeData(scopeData: ITemplateScopeData) {
    if (scopeData) {
      this.scopeData$.next(scopeData);
    }
  }

  @Input()
  set scope(scope: TemplateScope) {
    if (scope) {
      this.scope$.next(scope);
    }
  }

  @Input()
  set useRelativeTime(useRelativeTime: BooleanInput) {
    this.useRelativeTime$.next(coerceBooleanProperty(useRelativeTime));
  }

  @Input()
  set notification(notification: IAutomatedNotification) {
    if (notification) {
      this.notification$.next(notification);
    }
  }

  constructor(private _editorPresets: EditorPresetsService) {
    this.notification$
      .pipe(filterUndefined(), take(1), takeUntil(this._onDestroy$))
      .subscribe((notification) => this.form.patchValue(notification));

    combineLatest([this.triggerDate$, this.useRelativeTime$])
      .pipe(
        filter(
          ([triggerDate, useRelativeTime]) => !!triggerDate && !useRelativeTime
        ),
        takeUntil(this._onDestroy$)
      )
      .subscribe(([triggerDate]) => {
        if (!triggerDate) {
          return;
        }
        this.form.enableTriggerControls();
        this.form.controls.triggerDate.setValue(toMoment(triggerDate));
        this.form.controls.triggerTime.setValue(
          toMoment(triggerDate).format(TIME_FORMAT_24HR)
        );
      });

    this.useCustomTemplate$
      .pipe(withLatestFrom(this.scope$), takeUntil(this._onDestroy$))
      .subscribe(
        ([isCustomTemplate, scope]) =>
          void this._useCustomTemplate(isCustomTemplate, scope)
      );

    combineLatest([this.configRef$, this.notification$])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([configRef, notification]) => {
        const useCustomTemplate = !configRef || !!notification?.customTemplate;
        this.useCustomTemplate$.next(useCustomTemplate);
      });

    this.isMarketingContext$
      .pipe(isChanged$(), takeUntil(this._onDestroy$))
      .subscribe(() => this.useCustomTemplate$.next(true));

    this.config$ = this.configRef$.pipe(
      switchMap((configRef) => this._getConfig(configRef))
    );

    this.configTemplate$ = this.config$.pipe(
      switchMap((config) =>
        config?.templateRef
          ? Firestore.getDoc(config.templateRef)
          : of(undefined)
      )
    );

    this.hasSubjectInput$ = formControlChanges$(this.form.controls.type).pipe(
      startWith(this.notification$.value?.type ?? this.form.value.type),
      map((value) => value === AutomatedNotificationType.EMAIL)
    );

    this.hasSubjectInput$
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((hasSubject) => {
        this.form.controls.subject.setValidators(
          hasSubject ? [Validators.required] : []
        );
        this.form.controls.subject.updateValueAndValidity();
      });

    this.templateType$ = defer(() =>
      formControlChanges$(this.form.controls.type)
    ).pipe(
      distinctUntilChanged(),
      map((notificationType) =>
        AutomatedNotification.getTemplateType(notificationType)
      )
    );

    const resolvedScope$ = combineLatest([
      formControlChanges$(this.form.controls.customTemplate),
      this.scope$,
    ]).pipe(map(([customTemplate, scope]) => customTemplate?.scope ?? scope));

    //
    // TODO: Render based on actual scope data. https://app.clickup.com/t/860qzurhu
    this._scopeData$ = combineLatest([
      this.scopeData$,
      this.scope$,
      resolvedScope$,
    ]).pipe(
      map(([scopeData, originalScope, resolvedScope]) =>
        resolvedScope === originalScope ? scopeData : {}
      )
    );

    this.templateContext$ = resolvedScope$.pipe(
      isChanged$(),
      switchMap(async (scope) => MOCK_ALL_RESOLVER.resolve([scope]))
    );

    this.hasScope$ = this.scope$.pipe(
      map((scope) => scope !== TemplateScope.None)
    );

    this.availableConditions$ = this.scope$.pipe(
      map((scope) =>
        getScopedConditions(scope).filter(
          (condition) => condition !== ConditionLogicId.Never
        )
      )
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  clearTriggerDate(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.form.controls.triggerDate.reset();
  }

  requiredConditionsChange(event: ConditionLogicConfigurationCollection): void {
    this.form.controls.requiredConditions.setValue(event);
  }

  timingChange(event: IAutomationTiming): void {
    this.form.controls.timing.patchValue(event);
  }

  async submit(): Promise<IAutomatedNotificationReturnData | undefined> {
    if (this.form.invalid) {
      return;
    }

    const formValue = this.form.getRawValue();
    if (!formValue.customTemplate?.content) {
      formValue.customTemplate = undefined;
    }

    const notification = AutomatedNotification.init(formValue);
    if (notification.customTemplate) {
      notification.customTemplate.renderedTemplate = renderRawTemplate(
        {
          content: notification.customTemplate.content,
          scope: notification.customTemplate.scope ?? TemplateScope.None,
          type: AutomatedNotification.getTemplateType(notification.type),
          renderedTemplate: notification.customTemplate.renderedTemplate,
        },
        this._editorPresets.defaultToHTMLExtensions()
      );
    }

    const result: IAutomatedNotificationReturnData = { ...notification };
    const config = await snapshot(this.config$);
    if (config) {
      result.configRef = config.ref;
    }
    if (formValue.triggerDate) {
      const triggerDate = mergeDayAndTime(
        toMoment(formValue.triggerDate),
        moment(formValue.triggerTime, TIME_FORMAT_24HR)
      );
      result.triggerDate = toTimestamp(triggerDate);
    }

    return result;
  }

  private async _getConfig(
    configRef?: DocumentReference<IAutomationConfiguration>
  ): Promise<WithRef<IAutomatedNotificationConfiguration> | undefined> {
    if (!configRef) {
      return undefined;
    }
    const config = await Firestore.getDoc(configRef);
    if (!isAutomatedNotificationConfiguration(config)) {
      // eslint-disable-next-line no-console
      console.warn(
        `Expected Automated Notification Configuration: ${config.ref.path}`
      );
      return undefined;
    }
    return config as WithRef<IAutomatedNotificationConfiguration>;
  }

  private async _useCustomTemplate(
    useCustomTemplate: boolean,
    scope: TemplateScope
  ): Promise<void> {
    if (!useCustomTemplate) {
      this.form.enableCustomTemplateControls(false);
      return;
    }
    if (this.form.value.customTemplate?.content) {
      return;
    }
    const config = await snapshot(this.config$);
    if (!isAutomatedNotificationConfiguration(config)) {
      this.form.controls.customTemplate.patchValue({
        content: initVersionedSchema(),
        scope,
      });
      return;
    }
    this.form.patchValue({ type: config.type });

    const rawTemplate = await TemplateDefinition.resolveRawTemplate(
      await Firestore.getDoc(config.templateRef),
      await snapshot(this._scopeData$)
    );
    this.form.enableCustomTemplateControls(true, rawTemplate);
  }
}
